import { AccountHeader } from '@/components/AccountHeader';
import { AppFooter } from '@/components/AppFooter';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { MapPin, Plus, Loader2, Home, Building2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useEffect, useState, useCallback } from 'react';
import { AddressService, CustomerAddress } from '@/services/address.service';
import { AddAddressDrawer, AddAddressFormData } from '@/components/AddAddressDrawer';
import { useToast } from '@/hooks/use-toast';

const Addresses = () => {
  const { user, isAuthenticated, isInitializing } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [addresses, setAddresses] = useState<CustomerAddress[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showAddAddressDrawer, setShowAddAddressDrawer] = useState(false);
  const [addingAddress, setAddingAddress] = useState(false);

  useEffect(() => {
    // Wait for auth initialization to complete before checking authentication
    if (isInitializing) {
      return;
    }

    if (!isAuthenticated) {
      navigate('/');
      return;
    }

    fetchAddresses();
  }, [isAuthenticated, isInitializing, navigate, user?.id]);

  const fetchAddresses = useCallback(async () => {
    if (!user?.id) return;

    try {
      setIsLoading(true);
      const addressList = await AddressService.getCustomerAddresses(user.id);
      setAddresses(addressList);
    } catch (error) {
      console.error('Error fetching addresses:', error);
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Failed to load addresses. Please try again later.',
      });
    } finally {
      setIsLoading(false);
    }
  }, [user?.id, toast]);

  const handleAddAddress = async (formData: AddAddressFormData) => {
    if (!user?.id) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Please login to add an address',
      });
      return;
    }

    try {
      setAddingAddress(true);
      
      // Format address1 as array (address line 1, full address, landmark)
      const address1Array = [
        formData.address1,
        `${formData.address1}${formData.address2 ? ', ' + formData.address2 : ''}, ${formData.city}`,
        formData.landmark || ''
      ].filter(Boolean); // Remove empty strings
      
      await AddressService.createAddress({
        first_name: formData.first_name,
        last_name: formData.last_name,
        company_name: formData.company_name || undefined,
        address1: address1Array,
        city: formData.city,
        country: formData.country,
        country_name: formData.country === 'IN' ? 'India' : formData.country,
        phone: formData.phone,
        postcode: formData.postcode,
        state: formData.state,
        prefer_time: formData.delivery_time || undefined,
        prefer_delivery: formData.address_type, // 'home' or 'office'
        delivery_instructions: formData.delivery_instructions || undefined,
      });

      toast({
        title: 'Success',
        description: 'Address added successfully',
      });

      // Refresh addresses and close drawer
      await fetchAddresses();
      setShowAddAddressDrawer(false);
    } catch (error) {
      console.error('Error adding address:', error);
      toast({
        variant: 'destructive',
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to add address',
      });
    } finally {
      setAddingAddress(false);
    }
  };

  const formatAddress = (address: CustomerAddress) => {
    const parts = [
      address.address1,
      address.address2,
      address.city,
      address.state,
      address.postcode,
      address.country,
    ].filter(Boolean);
    return parts.join(', ');
  };

  // Show loading state while initializing auth
  if (isInitializing) {
    return (
      <div className="min-h-screen bg-background flex flex-col">
        <AccountHeader />
        <main className="flex-1 container mx-auto px-4 py-8 max-w-6xl">
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        </main>
        <AppFooter />
      </div>
    );
  }

  if (!isAuthenticated || !user) {
    return null;
  }

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <AccountHeader />
      <main className="flex-1 container mx-auto px-4 py-8 max-w-6xl">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold">My Addresses</h1>
            <p className="text-muted-foreground mt-2">Manage your delivery addresses</p>
          </div>
          <Button onClick={() => setShowAddAddressDrawer(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Add New Address
          </Button>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : addresses.length === 0 ? (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-12">
              <MapPin className="h-16 w-16 text-muted-foreground mb-4" />
              <h3 className="text-xl font-semibold mb-2">No addresses yet</h3>
              <p className="text-muted-foreground mb-4">Add your first delivery address to get started.</p>
              <Button onClick={() => setShowAddAddressDrawer(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Add Address
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {addresses.map((address) => (
              <Card key={address.id} className={`${address.default_address === 1 ? 'border-primary border-2' : ''}`}>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="flex items-center gap-2 text-lg">
                      {address.address_type === 'home' ? (
                        <Home className="h-5 w-5" />
                      ) : (
                        <Building2 className="h-5 w-5" />
                      )}
                      {address.address_type === 'home' ? 'Home' : 'Office'}
                    </CardTitle>
                    {address.default_address === 1 && (
                      <span className="px-2 py-1 text-xs font-medium bg-primary text-primary-foreground rounded">
                        Default
                      </span>
                    )}
                  </div>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div>
                    <p className="font-semibold">{address.first_name} {address.last_name}</p>
                    {address.company_name && (
                      <p className="text-sm text-muted-foreground">{address.company_name}</p>
                    )}
                  </div>
                  <div className="text-sm text-muted-foreground">
                    <p>{formatAddress(address)}</p>
                  </div>
                  <div className="text-sm">
                    <p className="font-medium">Phone: {address.phone}</p>
                    {address.email && (
                      <p className="text-muted-foreground">Email: {address.email}</p>
                    )}
                  </div>
                  {address.prefer_delivery && (
                    <div className="text-xs text-muted-foreground">
                      <p>Preference: {address.prefer_delivery}</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        <AddAddressDrawer
          open={showAddAddressDrawer}
          onOpenChange={setShowAddAddressDrawer}
          onSubmit={handleAddAddress}
          isLoading={addingAddress}
        />
      </main>
      <AppFooter />
    </div>
  );
};

export default Addresses;

